import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GET_USER_PROFILE } from 'projects/app-nmlsmod-profile/src/app/graphql/queries';
import {
  ModUserProfile,
  AssociationProfile,
  isErrorResponse,
} from 'projects/app-nmls-entity-profile/src/app/models/profiles.response';
import { mapNullValuesToEmptyString } from 'projects/app-nmls-entity-profile/src/app/utils/map-null-values.utils';
import { convertDateToIso } from 'projects/app-nmls-entity-profile/src/app/utils/date-validator.utils';
import { BehaviorSubject, map, Observable, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { UPDATE_USER_PROFILE } from 'projects/app-nmlsmod-profile/src/app/graphql/mutations';
import {
  UpdateAssociationProfile,
  UpdateUserProfileInput,
} from 'projects/app-nmlsmod-profile/src/app/models/profiles.response';
import { END_COMPANY_RELATIONSHIP } from 'projects/app-nmls-entity-profile/src/app/graphql/mutation';

@Injectable({
  providedIn: 'root',
})
export class ProfilesService {
  modUser: ModUserProfile;
  associatedClassicUsers: AssociationProfile[];
  private window = window;
  private location = location;
  private sessionCheckCache: { result: any; timestamp: number } | null = null;
  private readonly SESSION_CACHE_TTL = 30000;
  associationLookup: Record<string, AssociationProfile>;

  private individualSummarySubject = new BehaviorSubject<any>(null);
  individualSummary$ = this.individualSummarySubject.asObservable();

  constructor(private http: HttpClient) {
    this.associationLookup = {};
  }

  getUserProfile() {
    const query = GET_USER_PROFILE;
    const endpoint = `${environment.identity}/api`;

    return this.http
      .post(endpoint, { query }, { withCredentials: true, headers: { 'Content-Type': 'application/json' } })
      .pipe(
        map((resp: { errors?; data?; errorMessage: string; statusDescription: string }) => {
          if (resp.errors || resp.errorMessage || resp.statusDescription) {
            return resp;
          }
          const val = resp.data.getUserProfile;
          const mappedEntry = mapNullValuesToEmptyString(val);
          mappedEntry['associations'] = val.associations.map((association) => mapNullValuesToEmptyString(association));
          return mappedEntry;
        }),
        tap((resp: any) => {
          if (isErrorResponse(resp)) {
            console.log(resp);
          } else {
            this.modUser = resp;
            this.associatedClassicUsers = resp.associations;
          }
        }),
      );
  }

  updateUserProfile(user: UpdateUserProfileInput) {
    const query = UPDATE_USER_PROFILE;
    const endpoint = `${environment.identity}/api`;

    return this.http
      .post(
        endpoint,
        { query, variables: { user } },
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
      )
      .pipe(
        map((resp: { errors?; data?; errorMessage: string; statusDescription: string }) => {
          if (resp.errors || resp.errorMessage || resp.statusDescription) {
            return resp;
          }
          const val = resp.data.updateUserProfile;
          const mappedEntry = mapNullValuesToEmptyString(val);
          if (val.associations) {
            mappedEntry['associations'] = val.associations.map((association) =>
              mapNullValuesToEmptyString(association),
            );
          }
          return mappedEntry;
        }),
      );
  }

  updateUser(updatedUser: any) {
    const userProfileInput: UpdateUserProfileInput = {
      firstName: updatedUser.firstName,
      lastName: updatedUser.lastName,
      associations: updatedUser.associations.map(this.mapAssociation.bind(this)),
    };
    if (updatedUser.middleName) {
      userProfileInput.middleName = updatedUser.middleName || '';
    }
    if (updatedUser.suffix) {
      userProfileInput.suffix = updatedUser.suffix || '';
    }
    this.updateUserProfile(userProfileInput).subscribe((resp: any) => {
      console.log(resp);
    });
  }

  mapAssociation(association: any): any {
    const matchingAssociation = this.associationLookup[association.username];
    let updatedAssociation: UpdateAssociationProfile = {
      username: association.username,
      email: association.email,
      firstName: association.firstName,
      lastName: association.lastName,
    };
    if (matchingAssociation?.middleName || association.middleName) {
      updatedAssociation.middleName = association.middleName || '';
    }
    if (matchingAssociation?.suffix || association.suffix) {
      updatedAssociation.suffix = association.suffix || '';
    }
    if (matchingAssociation?.title || association.title) {
      updatedAssociation.title = association.title || '';
    }
    if (matchingAssociation?.phoneNumber || association.phoneNumber) {
      updatedAssociation.phoneNumber = association.phoneNumber || '';
    }
    return updatedAssociation;
  }

  checkSession(forceRefresh = false) {
    const now = Date.now();

    if (!forceRefresh && this.sessionCheckCache && now - this.sessionCheckCache.timestamp < this.SESSION_CACHE_TTL) {
      const cachedResult = this.sessionCheckCache.result;
      if (!(cachedResult?.sessionExists === 'Y') && !this.window.location.origin.includes('localhost')) {
        console.error('@mu3/fip-session: Unable to reauthenticate user. Logging out');
        globalThis.finformUnsavedChanges = false;
        this.location.href = environment.logout;
      }
      return of(cachedResult);
    }

    return this.http.get(`${environment.identity}/session/expirationTime`, { withCredentials: true }).pipe(
      tap((res: any) => {
        this.sessionCheckCache = { result: res, timestamp: now };
        if (!(res?.sessionExists === 'Y') && !this.window.location.origin.includes('localhost')) {
          console.error('@mu3/fip-session: Unable to reauthenticate user. Logging out');
          globalThis.finformUnsavedChanges = false;
          this.location.href = environment.logout;
        }
      }),
    );
  }

  getIndividualSummary(individualId: string | number, forceRefresh = false): Observable<any> {
    if (this.individualSummarySubject.value && !forceRefresh) {
      return this.individualSummary$;
    }

    const query = `
    query getIndividualSummary($individualId: Float!) {
      getIndividualSummary(individualId: $individualId) {
        individualId
        contactDetails { email { notificationEmail } }
        relationships { companyId initialTerminationDate }
      }
    }`;

    const variables = { individualId: Number(individualId) };
    const endpoint = environment.urls.entityProfileGraphql;

    const res = this.http.post<any>(
      endpoint,
      { query, variables },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      },
    );

    if (!forceRefresh) {
      res.subscribe({
        next: (response) => this.individualSummarySubject.next(response),
        error: (err) => console.error('Error fetching individual summary:', err),
      });
      return this.individualSummary$;
    }

    return res;
  }

  endCompanyRelationship(
    relationshipId: number,
    relationshipEndDate: string,
    endingReason: string,
    deceasedDate: string,
    endingExplanation: string,
  ) {
    const query = END_COMPANY_RELATIONSHIP;
    const variables = {
      relationshipId,
      relationshipEndDate: relationshipEndDate ? convertDateToIso(relationshipEndDate) : relationshipEndDate,
      endingReason,
      deceasedDate: deceasedDate ? convertDateToIso(deceasedDate) : deceasedDate,
      endingExplanation,
    };
    const endpoint = environment.urls.entityProfileGraphql;

    return this.http
      .post(endpoint, { query, variables }, { withCredentials: true, headers: { 'Content-Type': 'application/json' } })
      .pipe(
        map((resp: { errors?; data? }) => {
          if (resp.errors) return resp;
          return mapNullValuesToEmptyString(resp);
        }),
      );
  }
}
